import React, { useCallback, useEffect, useRef, useState } from "react"

import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material"
import { Formik } from "formik"
import { cloneDeep } from "lodash"
import { useDispatch, useSelector } from "react-redux"

import { getMachineTypesSelector } from "../../../../../reducers/Machines/machines"
import { getKitchenAreasListSelector } from "../../../../../reducers/Site/sites"
import {
  computeReusableProductionStepsNumbers,
  getReusableFormInitialValues
} from "../../../../../actions/Utils/utils"
import ProductionStepsTable from "../../../../../components/Recipe/ProductionSteps/ProductionStepsTable"
import ProductionStepsTableHead from "../../../../../components/Recipe/ProductionSteps/ProductionStepsTableHead"
import { ReusableProductionStepSchema } from "../../../../../utils/yupValidators"
import {
  createOrUpdateReusableProductionStep,
  duplicateReusableProductionStep,
  exportReusableStepUsage,
  rebuildRecipesAfterReusableStepUpdate,
  showReusableProductionStepEdition,
  showReusableProductionSteps
} from "../../../../../actions/Steps/reusableProductionSteps."
import {
  getIsEditionReusableProductionStepSelector, getIsNewReusableProductionStepSelector,
  getReusableProductionStepSelector
} from "../../../../../reducers/Steps/steps"
import ReusableStepFormRow from "./ReusableStepFormRow"
import GenericHeaderReturnButton from "../../../../../components/GenericHeaderReturnButton"
import GenericTopBarHeader from "../../../../../components/GenericTopBarHeader"
import { getTransformationModesSelector } from "../../../../../reducers/TransformationModes/transformationModes"
import { REUSABLE_STEPS_TABLE_HEADS } from "../../../../../utils/recipes"
import ReusableStepFormRightAction from "./ReusableStepFormRightAction"
import ReusableStepRebuildReportModal from "./ReusableStepRebuildReportModal"
import ReusableStepRebuildProgressDialog from "./ReusableStepRebuildProgressDialog"

const ReusableStepForm = ({ location }) => {
  const machineTypes = useSelector(getMachineTypesSelector)
  const kitchenAreas = useSelector(getKitchenAreasListSelector)
  const reusableStep = useSelector(getReusableProductionStepSelector)
  const isEdition = useSelector(getIsEditionReusableProductionStepSelector)
  const isNew = useSelector(getIsNewReusableProductionStepSelector)
  const transformationModes = useSelector(getTransformationModesSelector)

  const dispatch = useDispatch()

  const formRef = useRef()
  const [initialValues, setInitialValues] = useState(null)
  const [stepNumbers, setStepNumbers] = useState({})

  /*
   * this will not be changed,
   * unlike the initial values that changed every input of the form change
   */
  const [defaultValues, setDefaultValues] = useState(null)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [fieldFocused, setFieldFocused] = useState(false)
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false)
  const [openAddOptionModal, setOpenAddOptionModal] = useState(false)

  useEffect(() => {
    const initialFormValues = getReusableFormInitialValues(cloneDeep(reusableStep), isNew)
    const stepNumbersStorage = computeReusableProductionStepsNumbers(initialFormValues)

    setStepNumbers(stepNumbersStorage)
    setInitialValues(initialFormValues)
    setDefaultValues(cloneDeep(initialFormValues))
  }, [reusableStep])

  const onRowBlur = useCallback(() => {
    if (fieldFocused) return
    setHoveredRow(null)
  }, [fieldFocused])

  const toggleDuplicateModal = () => setOpenDuplicateModal(!openDuplicateModal)

  const toggleAddOptionModal = () => setOpenAddOptionModal(!openAddOptionModal)

  const handleCloseDuplicateModal = () => {
    if (openDuplicateModal) {
      setOpenDuplicateModal(false)
      return
    }

    if (openAddOptionModal) {
      setOpenAddOptionModal(false)
    }
  }

  /**
 * ComponentIndex is used for productionSteps > stepComponents
 * PriorComponentIndex is used for productionSteps > stepComponents > priorSteps
 */
  const onRowHover = useCallback((
    component,
    index,
    parentIndex = null,
    componentIndex = null,
    priorComponentIndex = null,
    stepComponentIndexesList = []

  ) => {
    if (fieldFocused) return
    setHoveredRow({
      component,
      index,
      parentIndex,
      componentIndex,
      priorComponentIndex,
      stepComponentIndexesList
    })
  }, [fieldFocused])

  const onFieldFocus = useCallback(() => setFieldFocused(true), [])

  const onFieldBlur = useCallback((event, setFieldTouched) => {
    setFieldFocused(false)
    setFieldTouched(event.target.name)
  }, [])

  const onKeyUp = useCallback((event, setFieldTouched) => {
    if (!setFieldTouched) return
    setFieldTouched(event.target.name)
  }, [])

  const handleSubmit = () => {
    if (!formRef.current) return
    formRef.current.handleSubmit()
  }

  const handleCancel = () => {
    setInitialValues(defaultValues)
    dispatch(showReusableProductionSteps())
  }

  const handleEdit = () => {
    setInitialValues(defaultValues)
    dispatch(showReusableProductionStepEdition(reusableStep.objectId))
  }

  const handleDuplicate = async () => {
    const shouldAddOption = openAddOptionModal
    await dispatch(duplicateReusableProductionStep(reusableStep.objectId, shouldAddOption))

    if (openAddOptionModal) {
      setOpenAddOptionModal(false)
      return
    }
    toggleDuplicateModal()
  }

  const _onSubmit = (values) => {
    dispatch(createOrUpdateReusableProductionStep(values))
  }

  const handleExportUsage = () => {
    dispatch(exportReusableStepUsage(reusableStep.objectId))
  }

  // KFC-565: also used to retry when the update after the save failed
  const handleRebuildRecipes = () => {
    dispatch(rebuildRecipesAfterReusableStepUpdate(reusableStep.objectId))
  }

  // KFC-565: the list, not the previous page (the edition, after a save)
  const onBack = () => dispatch(showReusableProductionSteps())

  const isViewOrEdit = location.pathname.includes("edit") || location.pathname.includes("view")

  return (
    <Box className="flexColumn" sx={{ width: "100%", height: "100%" }}>
      <Box style={{ height: 64, width: "100%" }}>
        <GenericTopBarHeader
          leftAction={!isEdition && !isNew ? (<GenericHeaderReturnButton handleClick={onBack} />) : null}
          rightAction={(
            <ReusableStepFormRightAction
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              menus={[
                { label: "Modifier cette étape réutilisable", onClick: handleEdit },
                { label: "Dupliquer cette étape réutilisable", onClick: toggleDuplicateModal },
                { label: "Créer une option à cette étape réutilisable", onClick: toggleAddOptionModal, key: "option" },
                { label: "Exporter les utilisations de l'étape réutilisable", onClick: handleExportUsage },
                { label: "Mettre à jour les recettes utilisant cette étape réutilisable", onClick: handleRebuildRecipes },
              ]}
            />
          )}
        />
      </Box>
      {/* table */}
      <ProductionStepsTable>
        {/* table head */}
        <ProductionStepsTableHead
          headers={REUSABLE_STEPS_TABLE_HEADS}
          hasAnyHoveredRow={!!hoveredRow}
        />
        <Box className="flexColumn">
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={ReusableProductionStepSchema}
            onSubmit={_onSubmit}
            validateOnChange={false}
            enableReinitialize
          >
            {({
              values,
              errors,
              setFieldValue,
              setFieldTouched,
              handleChange,
              setValues,
            }) => {
              const _onFieldBlur = useCallback((e) => onFieldBlur(e, setFieldTouched), [onFieldBlur, setFieldTouched])
              const _onKeyUp = useCallback((e) => onKeyUp(e, setFieldTouched), [onKeyUp, setFieldTouched])

              return (
                <ReusableStepFormRow
                  onRowHover={onRowHover}
                  onRowBlur={onRowBlur}
                  hoveredRow={hoveredRow}
                  onFieldFocus={onFieldFocus}
                  onFieldBlur={_onFieldBlur}
                  onKeyUp={_onKeyUp}
                  errors={errors}
                  machineTypes={machineTypes}
                  kitchenAreas={kitchenAreas}
                  transformationModes={transformationModes}
                  setFieldValue={setFieldValue}
                  stepValues={values}
                  setValues={setValues}
                  isEdition={isEdition}
                  fromRecipe={false}
                  handleChange={handleChange}
                  withMarginBottom={isViewOrEdit}
                  stepNumbers={stepNumbers}
                />
              )
            }}
          </Formik>
        </Box>
      </ProductionStepsTable>

      {/* duplicate or add option confirmation dialog */}
      <Dialog
        onClose={handleCloseDuplicateModal}
        aria-labelledby="duplicate-reusable-step-dialog"
        open={openDuplicateModal || openAddOptionModal}
      >
        <DialogContent>
          {openAddOptionModal
            ? "Souhaitez-vous créer une option à cette étape réutilisable ?"
            : "Voulez-vous dupliquer cette étape réutilisable ?"
          }
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDuplicateModal}
          >
            Annuler
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleDuplicate}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <ReusableStepRebuildProgressDialog />
      <ReusableStepRebuildReportModal />
    </Box>
  )
}

export default ReusableStepForm
